import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import apiRequest from "../../functions/api";
import { useMessage } from "../../context/message-context";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

const program = "KMPAPI";

export function VorgabenDialog({ open = false, handleClose, accessToken, client, vorgaben, reloadVorgaben }) {
  const { t } = useTranslation();
  const { showErrorMessage, showSuccessMessage } = useMessage();
  const [expanded, setExpanded] = useState(null);
  const [editVorgabe, setEditVorgabe] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    vorgabe: null,
    dbFeld: null,
  });
  const [deleteInfoDialog, setDeleteInfoDialog] = useState({
    open: false,
    vorgabe: null,
    kabel: [],
  });

  useEffect(() => {
    reloadVorgaben();
  }, []);

  // ------------------------------------------------------
  // -------------------Api Funktionen---------------------
  // ------------------------------------------------------

  const handleErstellen = (e) => {
    e.preventDefault();

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "VORGABEN_ERSTELLEN",
      vorgabe: editVorgabe,
      edit: isEdit,
    };

    apiRequest("POST", "daten", payload, headers, erstellenCb, errorCb);
  };

  const erstellenCb = (response) => {
    if (response.status === 200) {
      setEditDialogOpen(false);
      showSuccessMessage("Anfrage erfolgreich bearbeitet");
      reloadVorgaben();
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const confirmDelete = () => {
    setDeleteDialog((prev) => ({
      ...prev,
      open: false,
    }));

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program: program,
      statement: "VORGABE_DELETE",
      werte: deleteDialog,
    };

    apiRequest("POST", "daten", payload, headers, deleteCb, errorCb);
  };

  const deleteCb = (response) => {
    if (response.status === 200) {
      if (response.data.kabel) {
        // Vorgabe ist noch einem Kabel zugeordnet also darf sie nicht gelöscht werden
        setDeleteInfoDialog({
          open: true,
          vorgabe: deleteDialog.vorgabe,
          kabel: response.data.kabel,
        });
      } else {
        showSuccessMessage(t("Die Vorgabe wurde erfolgreich gelöscht!"));
        reloadVorgaben();
      }
    } else {
      showErrorMessage(response.data.message);
    }
  };

  const errorCb = () => {
    showErrorMessage(t("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut."));
  };

  // ------------------------------------------------------
  // ----------------Generelle Funktionen------------------
  // ------------------------------------------------------
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const editEntry = (vorgabe, dbFeld) => {
    setEditVorgabe({
      dbFeld: dbFeld,
      label: vorgabe.label,
      value: vorgabe.value,
    });
    setEditDialogOpen(true);
    setIsEdit(true);
  };

  const handleCreate = (dbFeld = null) => {
    setEditVorgabe({
      dbFeld,
      label: "",
      value: "",
    });
    setEditDialogOpen(true);
    setIsEdit(false);
  };

  const handleDelete = (vorgabe, dbFeld) => {
    setDeleteDialog({
      open: true,
      vorgabe,
      dbFeld,
    });
  };

  return (
    <>
      {/* Haupt Dialog */}
      <Dialog fullWidth={true} maxWidth="lg" open={open} onClose={handleClose}>
        <DialogTitle>{t("Erstellen und Editieren der Vorgaben")}</DialogTitle>

        <DialogContent
          sx={{
            p: 3,
            height: 800,
            overflowY: "auto",
          }}
        >
          <Stack spacing={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleCreate()}>
                Neue Vorgabe
              </Button>
            </Box>
            {vorgaben?.liste?.map((DB_FELD) => (
              <Accordion
                key={DB_FELD}
                expanded={expanded === DB_FELD}
                onChange={handleAccordionChange(DB_FELD)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                    {/* Linke Seite */}
                    <Stack spacing={0}>
                      <Typography fontWeight={600}>{vorgaben.bezeichnung[DB_FELD].titel}</Typography>

                      <Typography variant="body2" color="text.secondary">
                        {vorgaben.bezeichnung[DB_FELD].subTitel}
                      </Typography>
                    </Stack>

                    {/* Rechte Seite */}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={`${vorgaben[DB_FELD]?.length ?? 0}`} />

                      <IconButton
                        color="primary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreate(DB_FELD);
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {vorgaben[DB_FELD]?.map((vorgabe, index) => (
                      <Box key={vorgabe.value}>
                        <ListItem
                          secondaryAction={
                            <Stack direction="row" spacing={1}>
                              <IconButton color="primary" onClick={() => editEntry(vorgabe, DB_FELD)}>
                                <EditIcon />
                              </IconButton>

                              <IconButton color="error" onClick={() => handleDelete(vorgabe, DB_FELD)}>
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Stack>
                          }
                          sx={{
                            py: 1.5,
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <ListItemText primary={vorgabe.label} secondary={vorgabe.value} />
                        </ListItem>

                        {index < vorgaben[DB_FELD].length - 1 && <Divider />}
                      </Box>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={handleClose}>
            Fertig
          </Button>
        </DialogActions>
      </Dialog>

      {/* Erstellen/bearbeiten Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? "Vorgabe bearbeiten" : "Neue Vorgabe"}</DialogTitle>

        <DialogContent dividers>
          <form id="vorgabe-form" onSubmit={handleErstellen}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {isEdit && (
                <Alert severity="warning">
                  {t(
                    "Bitte ändern Sie eine Vorgabe nur bei Textkorrekturen (z.B. Rechtschreibfehler). Eine inhaltliche Änderung wird bei allen Kabeln übernommen, bei denen diese Vorgabe aktuell verwendet wird.",
                  )}
                </Alert>
              )}

              {vorgaben && (
                <TextField
                  select
                  required
                  disabled={isEdit}
                  name="Kategorie"
                  label="Kategorie"
                  value={editVorgabe?.dbFeld ? editVorgabe?.dbFeld : ""}
                  onChange={(e) =>
                    setEditVorgabe((prev) => ({
                      ...prev,
                      dbFeld: e.target.value,
                    }))
                  }
                  fullWidth
                >
                  {vorgaben?.liste?.map((DB_FELD, index) => {
                    return (
                      <MenuItem key={DB_FELD + index} value={DB_FELD}>
                        {vorgaben?.bezeichnung[DB_FELD].titel}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}

              <TextField
                label="Bezeichnung"
                value={editVorgabe.label}
                required
                onChange={(e) =>
                  setEditVorgabe((prev) => ({
                    ...prev,
                    label: e.target.value,
                  }))
                }
                fullWidth
              />
            </Stack>
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>

          <Button variant="contained" type="submit" form="vorgabe-form">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Löschen confirm Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, vorgabe: null, dbFeld: null })}>
        <DialogTitle>{t("Vorgabe löschen?")}</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("Möchten Sie die Vorgabe")} <strong>{deleteDialog.vorgabe?.label}</strong> {t("wirklich löschen?")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, vorgabe: null, dbFeld: null })}>Abbrechen</Button>

          <Button color="error" variant="contained" onClick={confirmDelete}>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beim Löschen wurde noch Kabel mit Vorgabe gefunden Dialog */}
      <Dialog
        open={deleteInfoDialog.open}
        onClose={() =>
          setDeleteInfoDialog({
            open: false,
            vorgabe: null,
            kabel: [],
          })
        }
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{t("Vorgabe kann nicht gelöscht werden")}</DialogTitle>

        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t("Die Vorgabe")} <strong>{deleteInfoDialog.vorgabe?.label}</strong> {t("wird derzeit von")} <strong>{deleteInfoDialog.kabel.length}</strong>{" "}
            {deleteInfoDialog.kabel.length === 1 ? t("Kabel") : t("Kabeln")} {t("verwendet und kann daher nicht gelöscht werden.")}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("Ändern Sie die Vorgabe zunächst bei den folgenden Kabeln.")}
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              overflowY: "auto",
            }}
          >
            <List dense disablePadding>
              {deleteInfoDialog.kabel.map((kabel, index) => (
                <Box key={kabel.ID}>
                  <ListItem>
                    <ListItemText primary={kabel.ID} />
                  </ListItem>

                  {index < deleteInfoDialog.kabel.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() =>
              setDeleteInfoDialog({
                open: false,
                vorgabe: null,
                kabel: [],
              })
            }
          >
            {t("Schließen")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
